import { create } from "zustand";
import { api, type Snapshot } from "./api";
import { playSound, unlockAudio } from "./audio";
import { pushBuildErrorToast } from "./budgetToast";
import { deriveStoryGate, isPanelAllowed } from "./storyMissionGuide";
import { parseMapTactical, type PlacementType } from "./mapTacticalUi";
import { persistTourIndex, readTourIndex, syncTourIndexFromSnapshot } from "./tutorialTour";

const RADIUS_FOCUS_KEY = "bda-radius-focus";

function readRadiusFocusPref(): boolean {
  try {
    const v = localStorage.getItem(RADIUS_FOCUS_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    /* ignore */
  }
  return false; // default: Abdeckung/Stadtgebiet aus
}

function persistRadiusFocusPref(on: boolean) {
  try {
    localStorage.setItem(RADIUS_FOCUS_KEY, on ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export type BuildMode =
  | null
  | {
      kind:
        | "fire_station"
        | "police_station"
        | "ems_station"
        | "thw"
        | "leitstelle"
        | "lagezentrum"
        | "workshop"
        | "school"
        | "hospital"
        | "jva"
        | "water_rescue"
        | "mountain_rescue"
        | "abschlepphof"
        | "bauhof"
        | "bahn_verladung";
      subtype: string;
      label: string;
    };

export type MapTool =
  | { type: "build"; mode: NonNullable<BuildMode> }
  | { type: "mark"; mode: NonNullable<BuildMode> }
  | { type: "claim"; mode: NonNullable<BuildMode> }
  | { type: "move"; buildingId: string }
  | { type: "water_suction"; incidentId: string }
  | null;

export type Panel =
  | null
  | "settings"
  | "news"
  | "faq"
  | "help"
  | "station"
  | "hr"
  | "verband"
  | "build"
  | "events"
  | "citizen"
  | "vehicle"
  | "incident"
  | "legal"
  | "appearance"
  | "finance"
  | "admin"
  | "katalog-editor"
  | "progress"
  | "stations"
  | "catalog"
  | "fleet"
  | "rewards"
  | "verwaltung"
  | "saison";

export type WikiTab = "articles" | "faq" | "catalog" | "legal";

export type UiToastTone = "info" | "success" | "warn" | "error";

type UiToast = {
  id: string;
  title?: string;
  message: string;
  tone: UiToastTone;
  durationMs?: number;
};

type UiConfirm = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  resolve: (ok: boolean) => void;
};

type UiPrompt = {
  title: string;
  message?: string;
  defaultValue?: string;
  resolve: (value: string | null) => void;
};

type ActivityPulse = {
  id: string;
  title: string;
  subtitle?: string;
  tone: "incident" | "radio" | "alarm" | "phone" | "success" | "info";
  at: number;
};

type State = {
  token: string | null;
  username: string | null;
  authUser: import("./api").AuthUser | null;
  snapshot: Snapshot | null;
  selectedIncidentId: string | null;
  incidentModalTab: string | null;
  selectedVehicleIds: string[];
  selectedBuildingId: string | null;
  detailVehicleId: string | null;
  mapTool: MapTool;
  /** Einsatzführung auf Karte — Verfolgung / Sammelstelle */
  mapTactical: {
    incidentId: string;
    tool: PlacementType | null;
    bindVehicleId?: string | null;
  } | null;
  panel: Panel;
  /** Aktiver Reiter im Wiki-Hub */
  wikiTab: WikiTab;
  theme: "dark" | "light";
  basemap: "street" | "satellite";
  radiusFocus: boolean;
  /** Feinschritt-Tutorial-Index (client) */
  tourIndex: number;
  /** Vom Coach gesetzter Wachen-Reiter */
  stationTourTab: string | null;
  /** Aktiver Reiter im Wachen-Detail (für Coach) */
  stationModalTab: string | null;
  /** Aktiver Reiter im Personal-Modal (für Coach) */
  hrModalTab: string | null;
  /** Gewählte Wache im Tab Besatzung (für Coach / Zuweisung) */
  hrAssignBuildingId: string | null;
  /** HR-Modal: Ziel-Reiter beim Öffnen (z. B. Besatzung von Wachübersicht) */
  hrFocusTab: "recruit" | "pool" | "roster" | "assign" | "payroll" | null;
  financeCurrency: "budget" | "renown" | "influence";
  /** Welcher Finanz-Tab (Budget / Ansehen / Einfluss) geoeffnet wird */
  vehicleVisualTick: number;
  error: string | null;
  knownIncidentIds: string[];
  uiToasts: UiToast[];
  uiConfirm: UiConfirm | null;
  uiPrompt: UiPrompt | null;
  activityPulse: ActivityPulse | null;
  setAuth: (token: string, username: string, user?: import("./api").AuthUser | null) => void;
  logout: () => void;
  setAuthUser: (u: import("./api").AuthUser | null) => void;
  setSnapshot: (s: Snapshot | null) => void;
  clearPlayState: () => void;
  setError: (e: string | null) => void;
  pushToast: (opts: {
    message: string;
    title?: string;
    tone?: UiToastTone;
    durationMs?: number;
  }) => void;
  dismissToast: (id: string) => void;
  pulseActivity: (opts: { title: string; subtitle?: string; tone?: ActivityPulse["tone"] }) => void;
  dismissActivityPulse: () => void;
  requestConfirm: (opts: {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    danger?: boolean;
  }) => Promise<boolean>;
  requestPrompt: (opts: {
    title: string;
    message?: string;
    defaultValue?: string;
  }) => Promise<string | null>;
  resolveConfirm: (ok: boolean) => void;
  resolvePrompt: (value: string | null) => void;
  selectIncident: (id: string | null) => void;
  selectBuilding: (id: string | null) => void;
  openStation: (buildingId?: string) => void;
  openVehicle: (vehicleId: string) => void;
  clearVehicleFocus: () => void;
  openIncident: (incidentId: string) => void;
  openIncidentAlarm: (incidentId: string) => void;
  openIncidentStabsMap: (
    incidentId: string,
    tool?: import("./mapTacticalUi").PlacementType | null,
  ) => void;
  toggleVehicle: (id: string) => void;
  setMapTool: (m: MapTool) => void;
  setMapTactical: (
    v: {
      incidentId: string;
      tool: PlacementType | null;
      bindVehicleId?: string | null;
    } | null,
  ) => void;
  setIncidentModalTab: (tab: string | null) => void;
  setPanel: (p: Panel) => void;
  setWikiTab: (tab: WikiTab) => void;
  setTheme: (t: "dark" | "light") => void;
  setBasemap: (m: "street" | "satellite") => void;
  setRadiusFocus: (on: boolean, opts?: { persist?: boolean }) => void;
  setTourIndex: (i: number) => void;
  advanceTour: () => void;
  resetTour: () => void;
  setStationTourTab: (t: string | null) => void;
  setStationModalTab: (t: string | null) => void;
  setHrModalTab: (t: string | null) => void;
  setHrAssignBuildingId: (id: string | null) => void;
  setHrFocusTab: (t: State["hrFocusTab"]) => void;
  setFinanceCurrency: (c: "budget" | "renown" | "influence") => void;
  bumpVehicleVisuals: () => void;
  dispatchSelected: () => Promise<void>;
  hireAt: (buildingId: string, role?: string) => Promise<void>;
  upgradeSelected: () => Promise<void>;
  buyModuleSelected: (moduleId: string) => Promise<void>;
  expandSlotsSelected: (kind: "vehicle" | "trailer" | "ab" | "rc") => Promise<void>;
  buyForSelected: (type: string) => Promise<void>;
  renameSelectedVehicle: (vehicleId: string, callsign: string) => Promise<void>;
  transferVehicle: (vehicleId: string, toBuildingId: string) => Promise<void>;
  railTransferVehicle: (vehicleId: string, toBuildingId: string) => Promise<void>;
  maintainVehicle: (vehicleId: string) => Promise<void>;
  refillVehicle: (vehicleId: string) => Promise<void>;
  returnVehicleHome: (vehicleId: string) => Promise<void>;
  attachLeitstelle: (buildingId: string) => Promise<void>;
  convertVehicle: (vehicleId: string) => Promise<void>;
  decideCitizen: (requestId: string, decision: "accept" | "reject") => Promise<void>;
  activatePlannedIncident: (incidentId: string, prepDone?: string[]) => Promise<void>;
  transferCustodyToJva: (detaineeId: string, toBuildingId?: string) => Promise<void>;
  custodySendToCourt: (detaineeId: string) => Promise<void>;
  custodyRelease: (detaineeId: string) => Promise<void>;
  trainPersonnel: (personnelId: string) => Promise<void>;
  setPersonnelRole: (personnelId: string, role: string) => Promise<void>;
  assignPersonnel: (personnelId: string, vehicleId: string) => Promise<void>;
  unassignPersonnel: (personnelId: string) => Promise<void>;
  autoAssignCrew: (opts: {
    buildingId?: string;
    vehicleId?: string;
  }) => Promise<{ assigned: number; error?: string }>;
  ensureMinDutyCrew: (opts?: {
    buildingId?: string;
  }) => Promise<{ woken: number; assigned: number; error?: string }>;
  launchImageCampaign: () => Promise<void>;
  startCampaign: (
    kind: "flyer" | "ad" | "campaign",
    org: "fire" | "police" | "ems" | "thw",
  ) => Promise<void>;
  hireApplicant: (applicantId: string, buildingId: string) => Promise<void>;
  inviteApplicantInterview: (applicantId: string) => Promise<void>;
  rejectApplicant: (applicantId: string) => Promise<void>;
  setPersonnelShift: (personnelId: string, status: "idle" | "off_duty") => Promise<void>;
  takeLoan: (amount: number) => Promise<void>;
  repayLoan: (amount?: number) => Promise<void>;
  cityGrant: () => Promise<void>;
  signContract: (contractId: string) => Promise<void>;
  incidentSupport: (
    action:
      | "tow"
      | "civil_works"
      | "water_assist"
      | "roadblock"
      | "detour"
      | "traffic_light"
      | "street_clean"
      | "utilities"
      | "thw_consult"
      | "crisis_intervention"
      | "care_tent"
      | "nea_light"
      | "funeral"
      | "pol_patrol"
      | "kripo_scene"
      | "ktu_investigation"
      | "pol_helo",
  ) => Promise<void>;
  unlockCompetence: (competenceId: string) => Promise<void>;
  dispatchVehiclesToIncident: (
    incidentId: string,
    vehicleIds: string[],
    mode?: "auto" | "queue" | "divert",
  ) => Promise<void>;
  dispatchByTypeToIncident: (
    incidentId: string,
    opts?: { byType?: Record<string, number>; mode?: "auto" | "queue" | "divert"; aao?: boolean },
  ) => Promise<void>;
};

const saved = (() => {
  try {
    return JSON.parse(localStorage.getItem("bda-auth") ?? "null") as {
      token: string;
      username: string;
      user?: import("./api").AuthUser;
    } | null;
  } catch {
    return null;
  }
})();

const savedTheme = (localStorage.getItem("bda-theme") as "dark" | "light" | null) ?? "dark";

export const useGame = create<State>((set, get) => ({
  token: saved?.token ?? null,
  username: saved?.username ?? null,
  authUser: saved?.user ?? null,
  snapshot: null,
  selectedIncidentId: null,
  incidentModalTab: null,
  selectedVehicleIds: [],
  selectedBuildingId: null,
  detailVehicleId: null,
  mapTool: null,
  mapTactical: null,
  panel: null,
  wikiTab: "articles",
  theme: savedTheme,
  basemap: (localStorage.getItem("bda-basemap") as "street" | "satellite") || "street",
  radiusFocus: readRadiusFocusPref(),
  tourIndex: 0,
  stationTourTab: null,
  stationModalTab: null,
  hrModalTab: null,
  hrAssignBuildingId: null,
  hrFocusTab: null,
  financeCurrency: "budget",
  vehicleVisualTick: 0,
  error: null,
  knownIncidentIds: [],
  uiToasts: [],
  uiConfirm: null,
  uiPrompt: null,
  activityPulse: null,
  setAuth: (token, username, user = null) => {
    localStorage.setItem("bda-auth", JSON.stringify({ token, username, user }));
    set({ token, username, authUser: user });
  },
  setAuthUser: (user) => {
    const token = get().token;
    const username = get().username;
    if (token && username) {
      localStorage.setItem("bda-auth", JSON.stringify({ token, username, user }));
    }
    set({ authUser: user });
  },
  logout: () => {
    localStorage.removeItem("bda-auth");
    set({
      token: null,
      username: null,
      authUser: null,
      snapshot: null,
      mapTool: null,
      knownIncidentIds: [],
    });
  },
  setSnapshot: (snapshot) => {
    if (!snapshot) {
      set({ snapshot: null, error: null, knownIncidentIds: [] });
      return;
    }
    const was = get().snapshot;
    // Verhindert Online-Race: alter Tick-WS überschreibt frischen Bau-Snapshot
    if (was && was.world.id === snapshot.world.id) {
      const wasT = Date.parse(String((was.world as { updatedAt?: string }).updatedAt ?? 0));
      const nextT = Date.parse(String((snapshot.world as { updatedAt?: string }).updatedAt ?? 0));
      if (Number.isFinite(wasT) && Number.isFinite(nextT) && nextT < wasT) {
        return;
      }
      if (
        Number.isFinite(wasT) &&
        Number.isFinite(nextT) &&
        nextT === wasT &&
        snapshot.buildings.length < was.buildings.length
      ) {
        return;
      }
      // Gleicher updatedAt, aber Bau schon im Client: WS-Tick ohne neues Gebäude verwerfen
      if (
        snapshot.buildings.length < was.buildings.length &&
        was.buildings.some((b) => !snapshot.buildings.find((x) => x.id === b.id))
      ) {
        return;
      }
    }
    const prevIds = get().knownIncidentIds;
    const incidents = snapshot.incidents ?? [];
    const ids = incidents
      .filter((i) => i.status === "open" || i.status === "active")
      .map((i) => i.id);

    if (prevIds.length > 0) {
      if (ids.some((id) => !prevIds.includes(id))) {
        playSound("incident");
        const fresh = incidents.find(
          (i) => !prevIds.includes(i.id) && (i.status === "open" || i.status === "active"),
        );
        get().pulseActivity({
          title: "Neuer Einsatz",
          subtitle: fresh?.title,
          tone: "incident",
        });
      }
    }
    if (was) {
      for (const v of snapshot.vehicles ?? []) {
        const old = was.vehicles.find((x) => x.id === v.id);
        if (old?.status === "enroute" && v.status === "onscene") playSound("arrive");
      }
      for (const i of incidents) {
        const old = was.incidents.find((x) => x.id === i.id);
        if (old && old.status !== "resolved" && i.status === "resolved") {
          playSound("resolve");
          // Kein Pulse — zu viele „Einsatz erledigt“-Banner
        }
        const mt = parseMapTactical(i.supportJson);
        const prevMt = old ? parseMapTactical(old.supportJson) : null;
        if (mt?.outcome === "fail_soft" && prevMt?.outcome !== "fail_soft") {
          get().pushToast({
            tone: "warn",
            title: "Verfolgung",
            message:
              "Flüchtiger ist entkommen — Nachalarm nötig, Auszahlung mit Malus (−1 Ansehen, längere Abarbeitung).",
            durationMs: 14_000,
          });
        }
      }
      const prevRadio = new Set((was.radioCalls ?? []).map((r) => r.id));
      for (const r of snapshot.radioCalls ?? []) {
        if (prevRadio.has(r.id)) continue;
        if (r.kind === "anruf") {
          playSound("phone");
          get().pulseActivity({ title: r.title, subtitle: r.fromLabel, tone: "phone" });
        } else if (r.kind === "status5") {
          playSound("status5");
          get().pulseActivity({ title: "Status 5", subtitle: r.fromLabel, tone: "radio" });
        } else if (/Abschlepp/i.test(r.title + (r.body || ""))) {
          playSound("tow");
          get().pulseActivity({ title: "Abschlepp-Anfrage", subtitle: r.fromLabel, tone: "radio" });
        } else if (r.kind === "sprechwunsch" || r.kind === "custody") {
          playSound("sprechwunsch");
          get().pulseActivity({
            title: r.kind === "custody" ? "Gewahrsam" : "Sprechwunsch",
            subtitle: r.fromLabel,
            tone: "radio",
          });
        } else if (r.kind === "support") {
          playSound("support");
          // support ohne Banner
        } else {
          playSound("radio");
        }
      }
      const prevMalusAt = (was.world.story as { autoRelianceMalusAt?: number } | undefined)
        ?.autoRelianceMalusAt;
      const nextMalusAt = (snapshot.world.story as { autoRelianceMalusAt?: number } | undefined)
        ?.autoRelianceMalusAt;
      if (nextMalusAt && nextMalusAt !== prevMalusAt) {
        const auto = snapshot.world.story?.autoDispatchCount ?? 0;
        const manual = snapshot.world.story?.manualDispatchCount ?? 0;
        const total = auto + manual;
        const pct = total > 0 ? Math.round((auto / total) * 100) : 0;
        get().pushToast({
          tone: "warn",
          title: "Autopilot-Überhang",
          message: `Zu viel Auto-Alarm (~${pct} %) — −1 Ansehen. Manuell alarmieren hält die Leitstelle wach.`,
          durationMs: 10_000,
        });
      }
      const prevHard = (was.world.story as { trainingGatesHardAt?: number } | undefined)
        ?.trainingGatesHardAt;
      const nextHard = (snapshot.world.story as { trainingGatesHardAt?: number } | undefined)
        ?.trainingGatesHardAt;
      if (nextHard && nextHard !== prevHard) {
        get().pushToast({
          tone: "info",
          title: "Ausbildung zählt",
          message:
            "Lehrgangs-Gates sind jetzt hart: DLK/TLF/HLF brauchen Maschinist — Basis-LF weiterhin ohne. Werben Sie Spezialisten ab Level 5.",
          durationMs: 12_000,
        });
      }
    }
    // Do not clear `error` here — WS/tick refreshes would dismiss build/budget hints instantly.
    const patch: Record<string, unknown> = { snapshot, knownIncidentIds: ids };

    const worldId = snapshot.world.id;
    const worldChanged = !was || was.world.id !== worldId;
    const baseTour = worldChanged ? readTourIndex(worldId) : get().tourIndex;
    const syncedTour = syncTourIndexFromSnapshot(snapshot, baseTour);
    if (syncedTour !== get().tourIndex || worldChanged) {
      persistTourIndex(syncedTour, worldId);
      patch.tourIndex = syncedTour;
    }
    set(patch as any);
  },
  clearPlayState: () => {
    persistTourIndex(0);
    set({
      snapshot: null,
      selectedIncidentId: null,
      selectedVehicleIds: [],
      selectedBuildingId: null,
      mapTool: null,
      panel: null,
      error: null,
      knownIncidentIds: [],
      tourIndex: 0,
      stationTourTab: null,
      stationModalTab: null,
      hrModalTab: null,
      hrAssignBuildingId: null,
      financeCurrency: "budget",
      radiusFocus: readRadiusFocusPref(),
    });
  },
  setError: (error) => set({ error }),
  pushToast: ({ message, title, tone = "info", durationMs }) =>
    set((s) => ({
      uiToasts: [
        ...s.uiToasts,
        {
          id: `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          message,
          title,
          tone,
          durationMs,
        },
      ],
    })),
  dismissToast: (id) => set((s) => ({ uiToasts: s.uiToasts.filter((t) => t.id !== id) })),
  pulseActivity: ({ title, subtitle, tone = "info" }) =>
    set({
      activityPulse: {
        id: `pulse-${Date.now()}`,
        title,
        subtitle,
        tone,
        at: Date.now(),
      },
    }),
  dismissActivityPulse: () => set({ activityPulse: null }),
  requestConfirm: (opts) =>
    new Promise((resolve) => {
      const prev = get().uiConfirm;
      if (prev) {
        set({ uiConfirm: null });
        try {
          prev.resolve(false);
        } catch {
          /* ignore */
        }
      }
      let settled = false;
      set({
        uiConfirm: {
          ...opts,
          resolve: (ok) => {
            if (settled) return;
            settled = true;
            set({ uiConfirm: null });
            resolve(ok);
          },
        },
      });
    }),
  requestPrompt: (opts) =>
    new Promise((resolve) => {
      set({
        uiPrompt: {
          ...opts,
          resolve: (value) => {
            set({ uiPrompt: null });
            resolve(value);
          },
        },
      });
    }),
  resolveConfirm: (ok) => {
    const c = get().uiConfirm;
    if (c) c.resolve(ok);
  },
  resolvePrompt: (value) => {
    const p = get().uiPrompt;
    if (p) p.resolve(value);
  },
  selectIncident: (selectedIncidentId) => set({ selectedIncidentId, selectedVehicleIds: [] }),
  selectBuilding: (selectedBuildingId) => set({ selectedBuildingId }),
  openStation: (buildingId) => {
    unlockAudio();
    const snap = get().snapshot;
    if (snap && !isPanelAllowed(snap, "station")) {
      const gate = deriveStoryGate(snap);
      get().pushToast({ title: "Story-Auftrag", message: gate.message, tone: "warn", durationMs: 9000 });
      return;
    }
    if (buildingId) set({ selectedBuildingId: buildingId, panel: "station" });
    else set({ panel: "station" });
  },
  openVehicle: (vehicleId) => {
    unlockAudio();
    // Karten-Dock links — Modal schließen damit Route auf der Karte sichtbar ist
    set({ detailVehicleId: vehicleId, panel: null });
  },
  clearVehicleFocus: () => set({ detailVehicleId: null }),
  openIncident: (incidentId) => {
    unlockAudio();
    // Einsatz-Detail ist Kern-Gameplay — nie durch Story-Gates blockieren
    set({ selectedIncidentId: incidentId, selectedVehicleIds: [], panel: "incident", incidentModalTab: "lage" });
  },
  openIncidentAlarm: (incidentId) => {
    unlockAudio();
    set({
      selectedIncidentId: incidentId,
      selectedVehicleIds: [],
      panel: "incident",
      incidentModalTab: "alarm",
    });
  },
  openIncidentStabsMap: (incidentId, tool = null) => {
    unlockAudio();
    set({
      selectedIncidentId: incidentId,
      selectedVehicleIds: [],
      panel: "incident",
      incidentModalTab: "karte",
      mapTactical: { incidentId, tool, bindVehicleId: null },
      mapTool: null,
    });
  },
  toggleVehicle: (id) => {
    unlockAudio();
    const cur = get().selectedVehicleIds;
    set({
      selectedVehicleIds: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    });
  },
  setMapTool: (mapTool) => {
    const snap = get().snapshot;
    const gate = deriveStoryGate(snap);
    if (snap && gate.hard && mapTool) {
      get().pushToast({
        title: "Story-Auftrag",
        message: gate.message,
        tone: "warn",
        durationMs: 9000,
      });
      return;
    }
    set({ mapTool });
  },
  setMapTactical: (mapTactical) => set({ mapTactical, mapTool: null }),
  setIncidentModalTab: (incidentModalTab) => set({ incidentModalTab }),
  setPanel: (panel) => {
    const snap = get().snapshot;
    let resolved: Panel = panel;
    let wikiTab = get().wikiTab;
    if (panel === "faq") {
      resolved = "help";
      wikiTab = "faq";
    } else if (panel === "legal" && snap) {
      resolved = "help";
      wikiTab = "legal";
    } else if (panel === "catalog") {
      resolved = "help";
      wikiTab = "catalog";
    } else if (panel === "help") {
      wikiTab = "articles";
    }
    if (snap && resolved && !isPanelAllowed(snap, resolved)) {
      const gate = deriveStoryGate(snap);
      get().pushToast({ title: "Story-Auftrag", message: gate.message, tone: "warn", durationMs: 9000 });
      return;
    }
    if (resolved !== "incident") {
      set({ panel: resolved, wikiTab, selectedIncidentId: null, selectedVehicleIds: [] });
      return;
    }
    set({ panel: resolved, wikiTab });
  },
  setWikiTab: (wikiTab) => set({ wikiTab, panel: "help" }),
  setTheme: (theme) => {
    localStorage.setItem("bda-theme", theme);
    document.documentElement.dataset.theme = theme;
    set({ theme });
  },
  setBasemap: (basemap) => {
    localStorage.setItem("bda-basemap", basemap);
    set({ basemap });
  },
  setRadiusFocus: (radiusFocus, opts) => {
    if (opts?.persist !== false) persistRadiusFocusPref(radiusFocus);
    set({ radiusFocus });
  },
  setTourIndex: (tourIndex) => {
    persistTourIndex(tourIndex, get().snapshot?.world.id);
    set({ tourIndex });
  },
  advanceTour: () => {
    const next = get().tourIndex + 1;
    persistTourIndex(next, get().snapshot?.world.id);
    set({ tourIndex: next });
  },
  resetTour: () => {
    persistTourIndex(0, get().snapshot?.world.id);
    persistTourIndex(0);
    set({ tourIndex: 0, radiusFocus: readRadiusFocusPref() });
  },
  setStationTourTab: (stationTourTab) => set({ stationTourTab }),
  setStationModalTab: (stationModalTab) => set({ stationModalTab }),
  setHrModalTab: (hrModalTab) => set({ hrModalTab }),
  setHrAssignBuildingId: (hrAssignBuildingId) => set({ hrAssignBuildingId }),
  setHrFocusTab: (hrFocusTab) => set({ hrFocusTab }),
  setFinanceCurrency: (financeCurrency) => set({ financeCurrency }),
  bumpVehicleVisuals: () => set({ vehicleVisualTick: get().vehicleVisualTick + 1 }),
  dispatchSelected: async () => {
    const { token, snapshot, selectedIncidentId, selectedVehicleIds } = get();
    if (!token || !snapshot || !selectedIncidentId || !selectedVehicleIds.length) return;
    await get().dispatchVehiclesToIncident(selectedIncidentId, selectedVehicleIds, "auto");
  },
  dispatchVehiclesToIncident: async (incidentId, vehicleIds, mode = "auto") => {
    const { token, snapshot } = get();
    if (!token || !snapshot || !incidentId || !vehicleIds.length) return;
    try {
      unlockAudio();
      playSound("alarm");
      const next = await api.dispatch(token, snapshot.world.id, incidentId, vehicleIds, mode);
      get().setSnapshot(next);
      set({ selectedVehicleIds: [], selectedIncidentId: incidentId });
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  dispatchByTypeToIncident: async (incidentId, opts) => {
    const { token, snapshot } = get();
    if (!token || !snapshot || !incidentId) return;
    try {
      unlockAudio();
      playSound("alarm");
      const next = await api.dispatchByType(token, snapshot.world.id, incidentId, opts);
      get().setSnapshot(next);
      set({ selectedVehicleIds: [], selectedIncidentId: incidentId });
    } catch (e: any) {
      const msg = e.message ?? "Alarmierung fehlgeschlagen";
      get().pushToast({
        tone: "warn",
        title: "Alarmierung",
        message: msg,
        durationMs: 14_000,
      });
    }
  },
  hireAt: async (buildingId, role = "firefighter") => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.hire(token, snapshot.world.id, buildingId, role);
      set({ snapshot: next, error: null });
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  upgradeSelected: async () => {
    const { token, snapshot, selectedBuildingId } = get();
    if (!token || !snapshot || !selectedBuildingId) return;
    try {
      const next = await api.upgradeBuilding(token, snapshot.world.id, selectedBuildingId);
      set({ snapshot: next, error: null });
    } catch (e: any) {
      const msg = e.message ?? "Upgrade fehlgeschlagen";
      set({ error: msg });
      pushBuildErrorToast("Ausbau", msg);
    }
  },
  buyModuleSelected: async (moduleId) => {
    const { token, snapshot, selectedBuildingId } = get();
    if (!token || !snapshot || !selectedBuildingId) return;
    try {
      const next = await api.buyBuildingModule(
        token,
        snapshot.world.id,
        selectedBuildingId,
        moduleId,
      );
      set({ snapshot: next, error: null });
    } catch (e: any) {
      const msg = e.message ?? "Modul-Kauf fehlgeschlagen";
      set({ error: msg });
      pushBuildErrorToast("Modul", msg);
    }
  },
  expandSlotsSelected: async (kind) => {
    const { token, snapshot, selectedBuildingId } = get();
    if (!token || !snapshot || !selectedBuildingId) return;
    try {
      const next = await api.expandBuildingSlots(
        token,
        snapshot.world.id,
        selectedBuildingId,
        kind,
      );
      set({ snapshot: next, error: null });
    } catch (e: any) {
      const msg = e.message ?? "Slot-Ausbau fehlgeschlagen";
      set({ error: msg });
      pushBuildErrorToast("Stellplätze", msg);
    }
  },
  buyForSelected: async (type) => {
    const { token, snapshot, selectedBuildingId } = get();
    if (!token || !snapshot || !selectedBuildingId) return;
    try {
      const next = await api.buyVehicle(token, snapshot.world.id, selectedBuildingId, type);
      unlockAudio();
      playSound("purchase");
      set({ snapshot: next, error: null });
      get().pushToast({
        tone: "success",
        title: "Fahrzeug gekauft",
        message: type,
        durationMs: 2800,
      });
    } catch (e: any) {
      const msg = e.message ?? "Kauf fehlgeschlagen";
      set({ error: msg });
      get().pushToast({
        tone: "error",
        title: "Fahrzeugkauf",
        message: msg,
        durationMs: 14_000,
      });
    }
  },
  renameSelectedVehicle: async (vehicleId, callsign) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.renameVehicle(token, snapshot.world.id, vehicleId, callsign);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  transferVehicle: async (vehicleId, toBuildingId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.transferVehicle(token, snapshot.world.id, vehicleId, toBuildingId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  railTransferVehicle: async (vehicleId, toBuildingId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.railTransferVehicle(token, snapshot.world.id, vehicleId, toBuildingId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  maintainVehicle: async (vehicleId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.maintainVehicle(token, snapshot.world.id, vehicleId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  refillVehicle: async (vehicleId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.refillVehicle(token, snapshot.world.id, vehicleId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  returnVehicleHome: async (vehicleId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.returnVehicle(token, snapshot.world.id, vehicleId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  attachLeitstelle: async (buildingId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.attachLeitstelle(token, snapshot.world.id, buildingId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  convertVehicle: async (vehicleId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.convertVehicle(token, snapshot.world.id, vehicleId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  decideCitizen: async (requestId, decision) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.decideCitizen(token, snapshot.world.id, requestId, decision);
      get().setSnapshot(next);
      if (decision === "accept") {
        get().pushToast({
          tone: "success",
          title: "Zusage erteilt",
          message: "Geplanter Einsatz erscheint unter „Geplant“ — dort freigeben.",
        });
      }
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  activatePlannedIncident: async (incidentId, prepDone) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.activatePlannedIncident(
        token,
        snapshot.world.id,
        incidentId,
        prepDone,
      );
      get().setSnapshot(next);
      playSound("dispatch");
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  transferCustodyToJva: async (detaineeId, toBuildingId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.transferCustodyToJva(
        token,
        snapshot.world.id,
        detaineeId,
        toBuildingId,
      );
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  custodySendToCourt: async (detaineeId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.custodySendToCourt(token, snapshot.world.id, detaineeId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  custodyRelease: async (detaineeId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.custodyRelease(token, snapshot.world.id, detaineeId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  trainPersonnel: async (personnelId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.trainPersonnel(token, snapshot.world.id, personnelId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  setPersonnelRole: async (personnelId, role) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.setPersonnelRole(token, snapshot.world.id, personnelId, role);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  assignPersonnel: async (personnelId, vehicleId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.assignPersonnel(token, snapshot.world.id, personnelId, vehicleId);
      get().setSnapshot(next);
      get().pushToast({ tone: "success", message: "Person zugewiesen", durationMs: 2400 });
    } catch (e: any) {
      set({ error: e.message });
      get().pushToast({ tone: "warn", message: e.message ?? "Zuweisung fehlgeschlagen" });
    }
  },
  unassignPersonnel: async (personnelId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.unassignPersonnel(token, snapshot.world.id, personnelId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  autoAssignCrew: async (opts) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) {
      return { assigned: 0, error: "Kein Spielstand geladen" };
    }
    const beforeAssigned = snapshot.personnel.filter((p) => p.vehicleId).length;
    try {
      const next = await api.autoAssignCrew(token, snapshot.world.id, opts);
      get().setSnapshot(next);
      const afterAssigned = next.personnel.filter((p) => p.vehicleId).length;
      const delta = afterAssigned - beforeAssigned;
      if (delta > 0) {
        get().pushToast({
          tone: "success",
          message: `${delta} Kräfte zugewiesen`,
          durationMs: 3200,
        });
        return { assigned: delta };
      }
      const msg = "Keine Zuweisung — alle Fahrzeuge erreichen die Mindestbesatzung.";
      get().pushToast({ tone: "info", message: msg, durationMs: 3500 });
      return { assigned: 0 };
    } catch (e: any) {
      const msg = e.message ?? "Auto-Zuweisung fehlgeschlagen";
      set({ error: msg });
      get().pushToast({ tone: "warn", message: msg, durationMs: 4500 });
      return { assigned: 0, error: msg };
    }
  },
  ensureMinDutyCrew: async (opts) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) {
      return { woken: 0, assigned: 0, error: "Kein Spielstand geladen" };
    }
    const beforeAssigned = snapshot.personnel.filter((p) => p.vehicleId).length;
    try {
      const result = await api.ensureMinDutyCrew(token, snapshot.world.id, opts);
      get().setSnapshot(result.snapshot);
      const afterAssigned = result.snapshot.personnel.filter((p) => p.vehicleId).length;
      const delta = afterAssigned - beforeAssigned;
      const parts: string[] = [];
      if (result.woken > 0) parts.push(`${result.woken} in Dienst`);
      if (delta > 0) parts.push(`${delta} zugewiesen`);
      get().pushToast({
        tone: delta > 0 || result.woken > 0 ? "success" : "info",
        message: parts.length
          ? `Dienstplan: ${parts.join(", ")}`
          : "Dienstplan: Mindestbesatzung war bereits gedeckt",
        durationMs: 3800,
      });
      return { woken: result.woken, assigned: delta };
    } catch (e: any) {
      const msg = e.message ?? "Dienstplan fehlgeschlagen";
      set({ error: msg });
      get().pushToast({ tone: "warn", message: msg, durationMs: 4500 });
      return { woken: 0, assigned: 0, error: msg };
    }
  },
  launchImageCampaign: async () => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.launchImageCampaign(token, snapshot.world.id);
      unlockAudio();
      playSound("ui");
      get().setSnapshot(next);
      get().pushToast({
        tone: "success",
        message: "Image-Kampagne gestartet — bessere Bewerber im Personal-Pool.",
        durationMs: 4200,
      });
    } catch (e: any) {
      set({ error: e.message });
      get().pushToast({
        tone: "warn",
        message: e.message ?? "Image-Kampagne fehlgeschlagen",
        durationMs: 4500,
      });
    }
  },
  startCampaign: async (kind, org) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.recruitCampaign(token, snapshot.world.id, kind, org);
      unlockAudio();
      playSound("ui");
      get().setSnapshot(next);
      get().pushToast({
        tone: "success",
        message: "Werbekampagne gestartet — Bewerber kommen im Pool an.",
        durationMs: 4000,
      });
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  hireApplicant: async (applicantId, buildingId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.hireApplicant(token, snapshot.world.id, applicantId, buildingId);
      unlockAudio();
      playSound("purchase");
      get().setSnapshot(next);
      get().pushToast({ tone: "success", message: "Personal eingestellt.", durationMs: 3500 });
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  inviteApplicantInterview: async (applicantId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.inviteApplicantInterview(token, snapshot.world.id, applicantId);
      unlockAudio();
      playSound("ui");
      get().setSnapshot(next);
      get().pushToast({
        tone: "info",
        message: "Bewerbungsgespräch läuft — etwa 1 Minute.",
        durationMs: 4000,
      });
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  rejectApplicant: async (applicantId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.rejectApplicant(token, snapshot.world.id, applicantId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  setPersonnelShift: async (personnelId, status) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.setPersonnelShift(token, snapshot.world.id, personnelId, status);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  takeLoan: async (amount) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.takeLoan(token, snapshot.world.id, amount);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  repayLoan: async (amount) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.repayLoan(token, snapshot.world.id, amount);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  cityGrant: async () => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.cityGrant(token, snapshot.world.id);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  signContract: async (contractId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.signContract(token, snapshot.world.id, contractId);
      get().setSnapshot(next);
      if (contractId === "bmi_lf_foerderung") {
        get().pulseActivity({
          title: "BMI-Zuschuss: +280.000 €",
          tone: "success",
        });
      }
      if (contractId === "flaeche_org_foerderung") {
        get().pulseActivity({ title: "Flächenförderung verbucht", tone: "success" });
      }
      if (contractId === "rd_anschub_foerderung") {
        get().pulseActivity({ title: "RD-Anschub verbucht", tone: "success" });
      }
      if (contractId === "start_leitstelle_foerderung") {
        get().pulseActivity({ title: "Zuweisung Leitstelle verbucht", tone: "success" });
      }
      if (contractId === "start_wache_foerderung") {
        get().pulseActivity({ title: "Zuweisung erste Wache verbucht", tone: "success" });
      }
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  incidentSupport: async (action) => {
    const { token, snapshot, selectedIncidentId } = get();
    if (!token || !snapshot || !selectedIncidentId) return;
    try {
      const next = await api.incidentSupport(token, snapshot.world.id, selectedIncidentId, action);
      get().setSnapshot(next);
      if (action === "tow") {
        get().pulseActivity({ title: "Abschlepper unterwegs", tone: "success" });
      }
    } catch (e: any) {
      set({ error: e.message });
    }
  },
  unlockCompetence: async (competenceId) => {
    const { token, snapshot } = get();
    if (!token || !snapshot) return;
    try {
      const next = await api.unlockCompetence(token, snapshot.world.id, competenceId);
      get().setSnapshot(next);
    } catch (e: any) {
      set({ error: e.message });
    }
  },
}));

if (typeof document !== "undefined") {
  document.documentElement.dataset.theme = savedTheme;
}
